import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  BarChart2,
  Clock,
  RefreshCw,
  Search,
  TrendingUp,
  Users
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const REFRESH_INTERVAL_MS = 30000;
const ACTIVITY_LIMIT = 25;
const MEMBERS_LIMIT = 25;

const AdminGymMonitor = () => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [overviewData, setOverviewData] = useState([]);
  const [overviewCount, setOverviewCount] = useState(0);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  const [lastOverviewUpdate, setLastOverviewUpdate] = useState(null);

  const [selectedGymId, setSelectedGymId] = useState(null);

  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  const [activityData, setActivityData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  const [membersData, setMembersData] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [membersPage, setMembersPage] = useState(1);

  const [memberFilters, setMemberFilters] = useState({
    status: 'all',
    role: 'member',
    search: ''
  });
  const [activeSection, setActiveSection] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  const formatNumber = useCallback((value, options = {}) => {
    if (value === null || value === undefined) return '-';
    const formatter = new Intl.NumberFormat('it-IT', {
      maximumFractionDigits: options.maxDecimals ?? 0,
      minimumFractionDigits: options.minDecimals ?? 0
    });
    return formatter.format(value);
  }, []);

  const formatPercentage = useCallback((value) => {
    if (value === null || value === undefined) return '-';
    const formatter = new Intl.NumberFormat('it-IT', {
      style: 'percent',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0
    });
    return formatter.format(value / 100);
  }, []);

  const formatPercentValue = useCallback(
    (value, options = {}) => {
      if (value === null || value === undefined) return '-';
      const formatted = formatNumber(value, options);
      return `${formatted}%`;
    },
    [formatNumber]
  );

  const formatDateTime = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  }, []);

  const fetchOverview = useCallback(
    async (withLoading = true) => {
      if (!api) return;
      if (withLoading) setOverviewLoading(true);
      setOverviewError(null);
      try {
        const response = await api.get('/admin_gym_dashboard.php', {
          params: { action: 'overview_list' }
        });
        const gyms = response.data?.data?.gyms ?? [];
        setOverviewData(gyms);
        setOverviewCount(response.data?.data?.count ?? gyms.length);
        setLastOverviewUpdate(new Date());
      } catch (error) {
        console.error('AdminGymMonitor overview error:', error);
        setOverviewError(t('admin.gymMonitor.errors.overview'));
      } finally {
        if (withLoading) setOverviewLoading(false);
      }
    },
    [api, t]
  );

  const fetchDetails = useCallback(
    async (gymId, withLoading = true) => {
      if (!api || !gymId) return;
      if (withLoading) setDetailsLoading(true);
      setDetailsError(null);
      try {
        const response = await api.get('/admin_gym_dashboard.php', {
          params: {
            action: 'gym_details',
            gym_id: gymId,
            months: 6
          }
        });
        setDetailsData(response.data?.data ?? null);
      } catch (error) {
        console.error('AdminGymMonitor details error:', error);
        setDetailsError(t('admin.gymMonitor.errors.details'));
      } finally {
        if (withLoading) setDetailsLoading(false);
      }
    },
    [api, t]
  );

  const fetchActivity = useCallback(
    async (gymId, withLoading = true) => {
      if (!api || !gymId) return;
      if (withLoading) setActivityLoading(true);
      setActivityError(null);
      try {
        const response = await api.get('/admin_gym_dashboard.php', {
          params: {
            action: 'activity_feed',
            gym_id: gymId,
            limit: ACTIVITY_LIMIT,
            page: 1
          }
        });
        setActivityData(response.data?.data?.events ?? []);
      } catch (error) {
        console.error('AdminGymMonitor activity error:', error);
        setActivityError(t('admin.gymMonitor.errors.activity'));
      } finally {
        if (withLoading) setActivityLoading(false);
      }
    },
    [api, t]
  );

  const fetchMembers = useCallback(
    async (gymId, withLoading = true) => {
      if (!api || !gymId) return;
      if (withLoading) setMembersLoading(true);
      setMembersError(null);
      try {
        const params = {
          action: 'members',
          gym_id: gymId,
          limit: MEMBERS_LIMIT,
          page: membersPage
        };
        if (memberFilters.status !== 'all') params.status = memberFilters.status;
        if (memberFilters.role !== 'all') params.role = memberFilters.role;
        if (memberFilters.search) params.search = memberFilters.search;

        const response = await api.get('/admin_gym_dashboard.php', { params });
        const payload = response.data?.data ?? {};
        setMembersData(payload.members ?? []);
        setMembersTotal(payload.total ?? 0);
      } catch (error) {
        console.error('AdminGymMonitor members error:', error);
        setMembersError(t('admin.gymMonitor.errors.members'));
      } finally {
        if (withLoading) setMembersLoading(false);
      }
    },
    [api, memberFilters, membersPage, t]
  );

  useEffect(() => {
    fetchOverview(true);
    const interval = setInterval(() => fetchOverview(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchOverview]);

  useEffect(() => {
    if (!selectedGymId) return;
    fetchDetails(selectedGymId, true);
    fetchActivity(selectedGymId, true);
    fetchMembers(selectedGymId, true);
  }, [selectedGymId, fetchActivity, fetchDetails, fetchMembers]);

  useEffect(() => {
    if (!selectedGymId) return;
    fetchMembers(selectedGymId, true);
  }, [memberFilters, membersPage, selectedGymId, fetchMembers]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setMemberFilters((prev) => ({
        ...prev,
        search: searchTerm.trim()
      }));
      setMembersPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleGymSelect = (gym) => {
    navigate(`/admin/gyms/${gym.gym_id}`, { state: { gymName: gym.name } });
  };

  const handleRefreshDetails = () => {
    if (!selectedGymId) return;
    fetchDetails(selectedGymId, true);
    fetchActivity(selectedGymId, true);
    fetchMembers(selectedGymId, true);
  };

  const resetMembersPagination = () => setMembersPage(1);

  const handleStatusChange = (event) => {
    setMemberFilters((prev) => ({ ...prev, status: event.target.value }));
    resetMembersPagination();
  };

  const handleRoleChange = (event) => {
    setMemberFilters((prev) => ({ ...prev, role: event.target.value }));
    resetMembersPagination();
  };

  const handlePrevMembers = () => {
    setMembersPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextMembers = () => {
    const maxPage = Math.ceil(membersTotal / MEMBERS_LIMIT);
    setMembersPage((prev) => Math.min(maxPage, prev + 1));
  };

  const selectedGymInfo = useMemo(
    () => overviewData.find((gym) => gym.gym_id === selectedGymId),
    [overviewData, selectedGymId]
  );

  const kpiCards = useMemo(() => {
    if (!detailsData?.overview) return [];
    const overview = detailsData.overview;
    return [
      {
        key: 'totalMembers',
        label: t('admin.gymMonitor.details.kpi.totalMembers'),
        value: formatNumber(overview.total_members)
      },
      {
        key: 'newThisMonth',
        label: t('admin.gymMonitor.details.kpi.newThisMonth'),
        value: formatNumber(overview.new_this_month)
      },
      {
        key: 'newDelta',
        label: t('admin.gymMonitor.details.kpi.newDelta'),
        value: formatNumber(overview.new_delta, { maxDecimals: 0 })
      },
      {
        key: 'workoutsThisMonth',
        label: t('admin.gymMonitor.details.kpi.workoutsThisMonth'),
        value: formatNumber(overview.workouts_this_month)
      },
      {
        key: 'activeCourses',
        label: t('admin.gymMonitor.details.kpi.activeCourses'),
        value: formatNumber(overview.active_courses)
      },
      {
        key: 'retentionRate',
        label: t('admin.gymMonitor.details.kpi.retentionRate'),
        value: formatPercentage(overview.retention_rate)
      },
      {
        key: 'avgVisitsPerMember',
        label: t('admin.gymMonitor.details.kpi.avgVisitsPerMember'),
        value: formatNumber(overview.avg_visits_per_member, { maxDecimals: 2 })
      }
    ];
  }, [detailsData, formatNumber, formatPercentage, t]);

  const frequencyDistribution = useMemo(() => {
    const distribution = detailsData?.frequency_distribution?.distribution ?? {};
    return Object.entries(distribution).map(([category, values]) => ({
      category,
      count: values.count ?? 0,
      percentage: values.percentage ?? 0
    }));
  }, [detailsData]);

  const membersMaxPage = Math.max(1, Math.ceil(membersTotal / MEMBERS_LIMIT));

  const tabTriggerClass =
    'group relative flex items-center gap-2 rounded-xl border border-transparent px-4 py-2 text-sm font-medium text-gray-500 transition-all hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 data-[state=active]:border-blue-500/70 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg whitespace-nowrap flex-shrink-0';

  const renderOverviewTable = () => (
    <section className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6 border-b border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.gymMonitor.overview.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('admin.gymMonitor.description')}
          </p>
          {lastOverviewUpdate && (
            <p className="text-xs text-gray-400 mt-1">
              {t('admin.gymMonitor.lastUpdate')}: {formatDateTime(lastOverviewUpdate)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">{t('admin.gymMonitor.overview.autoRefresh')}</span>
          <button
            type="button"
            onClick={() => fetchOverview(true)}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.refresh')}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {overviewLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner size="lg" />
          </div>
        ) : overviewError ? (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-6">
            <AlertTriangle className="h-5 w-5" />
            <span>{overviewError}</span>
          </div>
        ) : overviewCount === 0 ? (
          <p className="p-6 text-sm text-gray-500 dark:text-gray-400">
            {t('admin.gymMonitor.overview.empty')}
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-gray-800/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.gymMonitor.overview.columns.gym')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">
                  {t('admin.gymMonitor.overview.columns.members')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">
                  {t('admin.gymMonitor.overview.columns.newMembers')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">
                  {t('admin.gymMonitor.overview.columns.workouts')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">
                  {t('admin.gymMonitor.overview.columns.capacity')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.gymMonitor.overview.columns.lastWorkout')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">
                  {t('admin.gymMonitor.overview.columns.avgVisits')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">
                  {t('admin.gymMonitor.overview.columns.retention')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
              {overviewData.map((gym) => {
                const capacityUsage =
                  gym.max_users && gym.max_users > 0 ? (gym.active_members / gym.max_users) * 100 : null;
                const cappedCapacityUsage =
                  capacityUsage !== null ? Math.min(100, Math.max(0, capacityUsage)) : null;
                return (
                  <tr
                    key={gym.gym_id}
                    onClick={() => handleGymSelect(gym)}
                    className="cursor-pointer transition hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{gym.name}</div>
                      <div className="text-xs text-gray-500">{gym.email}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">
                      {formatNumber(gym.active_members)}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">
                      {formatNumber(gym.new_members_30d)}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">
                      {formatNumber(gym.workouts_last_7d)}
                    </td>
                    <td className="px-6 py-4">
                      {gym.max_users ? (
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {formatNumber(gym.active_members)} / {formatNumber(gym.max_users)}
                          </div>
                          {cappedCapacityUsage !== null && (
                            <div className="mt-1 flex items-center justify-end gap-2">
                              <div className="w-24 h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                <div
                                  className={`h-full ${
                                    cappedCapacityUsage >= 90
                                      ? 'bg-red-500'
                                      : cappedCapacityUsage >= 75
                                      ? 'bg-amber-500'
                                      : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${cappedCapacityUsage.toFixed(1)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatPercentValue(cappedCapacityUsage, { maxDecimals: 1 })}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {gym.last_workout_at ? formatDateTime(gym.last_workout_at) : '—'}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">
                      {formatNumber(gym.avg_visits_30d, { maxDecimals: 2 })}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">
                      {formatPercentage(gym.retention_90d)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );

  const renderGymDetail = () => {
    if (!selectedGymId) return null;

    return (
      <section className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.gymMonitor.details.title')}
            </h2>
            {selectedGymInfo && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('admin.gymMonitor.details.selectedGym', { gym: selectedGymInfo.name })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleRefreshDetails}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.refresh')}
          </button>
        </div>

        {detailsLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner size="lg" />
          </div>
        ) : detailsError ? (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-6">
            <AlertTriangle className="h-5 w-5" />
            <span>{detailsError}</span>
          </div>
        ) : !detailsData ? (
          <p className="p-6 text-sm text-gray-500 dark:text-gray-400">-</p>
        ) : (
          <div className="p-6 space-y-6">
            {selectedGymInfo && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    {t('admin.gymMonitor.details.contact.title')}
                  </h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {t('admin.gymMonitor.details.contact.email')}
                      </dt>
                      <dd className="text-gray-900 dark:text-gray-100">{selectedGymInfo.email || '—'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {t('admin.gymMonitor.details.contact.phone')}
                      </dt>
                      <dd className="text-gray-900 dark:text-gray-100">{selectedGymInfo.phone || '—'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {t('admin.gymMonitor.details.contact.address')}
                      </dt>
                      <dd className="max-w-[65%] text-right text-gray-900 dark:text-gray-100">
                        {selectedGymInfo.address || '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    {t('admin.gymMonitor.details.contact.capacityTitle')}
                  </h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {t('admin.gymMonitor.details.contact.maxUsers')}
                      </dt>
                      <dd className="text-gray-900 dark:text-gray-100">
                        {selectedGymInfo.max_users ? formatNumber(selectedGymInfo.max_users) : '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {t('admin.gymMonitor.details.contact.maxTrainers')}
                      </dt>
                      <dd className="text-gray-900 dark:text-gray-100">
                        {selectedGymInfo.max_trainers ? formatNumber(selectedGymInfo.max_trainers) : '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {kpiCards.map((kpi) => (
                <div
                  key={kpi.key}
                  className="bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded-xl p-4"
                >
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-2">{kpi.label}</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{kpi.value}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('admin.gymMonitor.details.todayStats.title')}
                  </h3>
                </div>
                <dl className="space-y-3">
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.gymMonitor.details.todayStats.workouts')}
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatNumber(detailsData.today_stats?.workouts_today)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.gymMonitor.details.todayStats.sessions')}
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatNumber(detailsData.today_stats?.sessions_today)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.gymMonitor.details.todayStats.activeTrainers')}
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatNumber(detailsData.today_stats?.active_trainers)}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('admin.gymMonitor.details.memberTrend')}
                  </h3>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {(detailsData.member_trend ?? []).map((item) => (
                    <div key={item.month} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">{item.month_label}</span>
                      <span className="text-gray-900 dark:text-gray-100 font-medium">
                        {formatNumber(item.total_active)}{' '}
                        <span className="text-xs text-gray-400 ml-1">
                          +{formatNumber(item.new_members)}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('admin.gymMonitor.details.workoutTrend')}
                  </h3>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {(detailsData.workout_trend ?? []).map((item) => (
                    <div key={item.month} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">{item.month_label}</span>
                      <span className="text-gray-900 dark:text-gray-100 font-medium">
                        {formatNumber(item.workouts_count)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderActivitySection = () => (
    <section className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800">
      <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.gymMonitor.activity.title')}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => fetchActivity(selectedGymId, true)}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          {t('common.refresh')}
        </button>
      </div>
      <div className="max-h-[28rem] overflow-y-auto">
        {activityLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : activityError ? (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-6">
            <AlertTriangle className="h-5 w-5" />
            <span>{activityError}</span>
          </div>
        ) : activityData.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 dark:text-gray-400">
            {t('admin.gymMonitor.activity.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {activityData.map((event) => (
              <li key={`${event.type}-${event.event_id}`} className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {t(`admin.gymMonitor.activity.types.${event.type}`, event.type)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {event.actor_name || '-'}
                      {event.actor_email && (
                        <span className="text-xs text-gray-400 ml-2">{event.actor_email}</span>
                      )}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">{formatDateTime(event.timestamp)}</span>
                </div>
                {event.metric_value !== null && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {formatNumber(event.metric_value)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );

  const renderMembersSection = () => (
    <section className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800">
      <div className="p-6 border-b border-gray-100 dark:border-gray-800 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.gymMonitor.members.title')}
          </h3>
          <button
            type="button"
            onClick={() => fetchMembers(selectedGymId, true)}
            className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            {t('common.refresh')}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-1 bg-gray-50 dark:bg-gray-800/40">
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {formatNumber(membersTotal)}
            </span>
            <span>{t('admin.gymMonitor.members.total')}</span>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-1 bg-gray-50 dark:bg-gray-800/40">
            <span className="font-medium text-blue-600 dark:text-blue-400">
              {formatNumber(membersData.filter((member) => member.status === 'active').length)}
            </span>
            <span>{t('admin.gymMonitor.members.activeCount')}</span>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-1 bg-gray-50 dark:bg-gray-800/40">
            <span className="font-medium text-emerald-600 dark:text-emerald-400">
              {formatNumber(
                membersData.filter((member) => (member.role_in_gym || 'member') === 'member').length
              )}
            </span>
            <span>{t('admin.gymMonitor.members.onlyMembers')}</span>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('admin.gymMonitor.members.searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center text-xs text-gray-500 dark:text-gray-400 gap-2">
              {t('admin.gymMonitor.members.statusFilter.label')}
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
                value={memberFilters.status}
                onChange={handleStatusChange}
              >
                <option value="all">{t('admin.gymMonitor.members.statusFilter.all')}</option>
                <option value="active">{t('admin.gymMonitor.members.statusFilter.active')}</option>
                <option value="inactive">{t('admin.gymMonitor.members.statusFilter.inactive')}</option>
              </select>
            </label>
            <label className="flex items-center text-xs text-gray-500 dark:text-gray-400 gap-2">
              {t('admin.gymMonitor.members.roleFilter.label')}
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
                value={memberFilters.role}
                onChange={handleRoleChange}
              >
                <option value="all">{t('admin.gymMonitor.members.roleFilter.all')}</option>
                <option value="manager">{t('admin.gymMonitor.members.roleFilter.manager')}</option>
                <option value="trainer">{t('admin.gymMonitor.members.roleFilter.trainer')}</option>
                <option value="member">{t('admin.gymMonitor.members.roleFilter.member')}</option>
              </select>
            </label>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        {membersLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : membersError ? (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-6">
            <AlertTriangle className="h-5 w-5" />
            <span>{membersError}</span>
          </div>
        ) : membersData.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 dark:text-gray-400">
            {t('admin.gymMonitor.members.empty')}
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800 table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-800/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-[18%]">
                  {t('admin.gymMonitor.members.table.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-[22%]">
                  {t('admin.gymMonitor.members.table.email')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[12%]">
                  {t('admin.gymMonitor.members.table.role')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[10%]">
                  {t('admin.gymMonitor.members.table.status')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[16%]">
                  {t('admin.gymMonitor.members.table.joined')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase w-[16%]">
                  {t('admin.gymMonitor.members.table.lastWorkout')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right w-[6%]">
                  {t('admin.gymMonitor.members.table.workouts30d')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
              {membersData.map((member) => (
                <tr key={member.id}>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name || '-'}</p>
                    <p className="text-xs text-gray-400">{member.username}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    {member.email || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 uppercase">
                    {member.role_in_gym || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 uppercase">
                    {member.status || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    {member.joined_at ? formatDateTime(member.joined_at) : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    {member.last_workout_at ? formatDateTime(member.last_workout_at) : '—'}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-600 dark:text-gray-300">
                    {formatNumber(member.workouts_30d)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-300">
        <span>
          {membersTotal > 0 &&
            `${(membersPage - 1) * MEMBERS_LIMIT + 1}-${Math.min(
              membersPage * MEMBERS_LIMIT,
              membersTotal
            )} / ${membersTotal}`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrevMembers}
            disabled={membersPage === 1}
            className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm disabled:opacity-40"
          >
            ‹
          </button>
          <span>
            {membersPage}/{membersMaxPage}
          </span>
          <button
            type="button"
            onClick={handleNextMembers}
            disabled={membersPage >= membersMaxPage}
            className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );

  const renderInsightsSection = () => {
    if (!detailsData) {
      return (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800 p-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.gymMonitor.details.frequencyDistribution')}
              </h3>
            </div>
            <ul className="space-y-2 text-sm">
              {frequencyDistribution.map((item) => (
                <li key={item.category} className="flex items-center justify-between text-gray-700 dark:text-gray-300">
                  <span>{item.category}</span>
                  <span className="font-medium">
                    {formatNumber(item.count)}{' '}
                    <span className="text-xs text-gray-400">
                      ({formatNumber(item.percentage, { maxDecimals: 1 })}%)
                    </span>
                  </span>
                </li>
              ))}
              {frequencyDistribution.length === 0 && (
                <li className="text-sm text-gray-500 dark:text-gray-400">-</li>
              )}
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.gymMonitor.details.atRiskMembers')}
              </h3>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1 text-sm">
              {(detailsData.at_risk_members ?? []).map((member) => (
                <div key={member.id} className="flex justify-between">
                  <div className="text-gray-700 dark:text-gray-200">
                    <p className="font-medium">{member.name}</p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                  </div>
                  <div className="text-right text-gray-700 dark:text-gray-200">
                    <p className="font-medium">
                      {formatNumber(member.days_inactive)}{' '}
                      <span className="text-xs text-gray-400">d</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {member.last_workout_date ? formatDateTime(member.last_workout_date) : '—'}
                    </p>
                  </div>
                </div>
              ))}
              {(detailsData.at_risk_members ?? []).length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
              {t('admin.gymMonitor.details.topCourses')}
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1 text-sm">
              {(detailsData.top_courses ?? []).map((course) => (
                <div key={course.id} className="flex justify-between">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-100">{course.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{course.category}</p>
                  </div>
                  <div className="text-right text-gray-700 dark:text-gray-200">
                    <p className="font-medium">
                      {formatNumber(course.total_enrollments)}{' '}
                      <span className="text-xs text-gray-400">enr.</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatNumber(course.avg_occupation_pct, { maxDecimals: 1 })}%
                    </p>
                  </div>
                </div>
              ))}
              {(detailsData.top_courses ?? []).length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
              {t('admin.gymMonitor.details.topMembers')}
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1 text-sm">
              {(detailsData.top_members ?? []).map((member) => (
                <div key={member.id} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      #{member.rank} {member.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                  </div>
                  <div className="text-right text-gray-700 dark:text-gray-200">
                    <p className="font-medium">
                      {formatNumber(member.workout_days)} <span className="text-xs text-gray-400">days</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatNumber(member.total_workouts)} workouts
                    </p>
                  </div>
                </div>
              ))}
              {(detailsData.top_members ?? []).length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {t('admin.gymMonitor.clickRowHint')}
      </p>
      {renderOverviewTable()}
    </div>
  );
};

export default AdminGymMonitor;
